import { StatsigClient, StatsigOptions, StatsigUser } from '@statsig/js-client';
import * as Sentry from '@sentry/vue';
import { ENV } from '../../config/app-env';

let statsigClient: StatsigClient | undefined;

/**
 * Initialize Statsig client with environment configuration
 * @param user Optional user context to initialize with
 */
async function initStatsig(user?: StatsigUser) {
  if (statsigClient) return statsigClient;
  if (!ENV.STATSIG_CLIENT_KEY) {
    console.warn(
      '[Statsig] STATSIG_CLIENT_KEY is not defined - Statsig client will not be initialized',
    );
    return;
  }

  const options: StatsigOptions = {
    environment: { tier: ENV.CLEARBANC_ENV },
  };

  try {
    statsigClient = new StatsigClient(
      ENV.STATSIG_CLIENT_KEY,
      user ?? {},
      options,
    );
    await statsigClient.initializeAsync();
  } catch (error) {
    Sentry.captureException(error);
    statsigClient = undefined;
  }
}

export async function setupStatsigClient(user: StatsigUser): Promise<void> {
  if (!statsigClient) {
    await initStatsig(user);
  }

  await updateStatsigUser(user);
}

async function updateStatsigUser(user: StatsigUser): Promise<void> {
  if (!statsigClient) {
    console.warn('[STATSIG] Cannot update user - client not initialized');
    return;
  }

  try {
    await statsigClient.updateUserAsync(user);
  } catch (error) {
    console.warn('[STATSIG] Error updating user context:', error);
    Sentry.captureException(error);
  }
}

export function checkGate(gateName: string): boolean {
  if (!statsigClient) {
    console.warn(
      `[STATSIG] Cannot check gate '${gateName}' - client not initialized`,
    );
    return false;
  }
  try {
    return statsigClient.checkGate(gateName);
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
}

export function getExperiment(experimentName: string): any {
  if (!statsigClient) {
    console.warn(
      `[STATSIG] Cannot get experiment '${experimentName}' - client not initialized`,
    );
    return { get: () => null };
  }
  try {
    return statsigClient.getExperiment(experimentName);
  } catch (error) {
    Sentry.captureException(error);
    return { get: () => null };
  }
}

export function logEvent(
  eventName: string,
  value?: string | number,
  metadata?: Record<string, any>,
): void {
  if (!statsigClient) return;

  try {
    statsigClient.logEvent(eventName, value, metadata);
  } catch (error) {
    Sentry.captureException(error);
  }
}

export function shutdownStatsig(): void {
  try {
    statsigClient?.shutdown();
  } catch (error) {
    Sentry.captureException(error);
  } finally {
    statsigClient = undefined;
  }
}
